import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquareWarning, Camera, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { api, formatApiError } from "@/api/client";
import { trackEvent } from "@/lib/analytics";

// V1.1-Beta — Floating beta-feedback widget.
//
// Visibility rules:
//   - hidden on /admin/* routes (admins have richer triage tools in
//     /admin/beta-feedback and we don't want the button overlapping their UI)
//   - shown for every other route, including anonymous users on landing
//
// The widget is a single floating action button in the bottom-right corner.
// Clicking it opens a modal with category + severity + message + optional
// screenshot. Submission posts to `POST /api/feedback` as multipart.

const CATEGORIES = [
  { value: "bug", label: "Bug", color: "#FCA5A5" },
  { value: "suggestion", label: "Suggestion", color: "#A5B4FC" },
  { value: "confused", label: "I'm confused", color: "#FCD34D" },
  { value: "other", label: "Other", color: "#86EFAC" },
];

const SEVERITIES = ["low", "medium", "high", "critical"];

export default function BetaFeedbackButton() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Hide on admin routes entirely.
  if ((location.pathname || "").startsWith("/admin")) return null;

  return (
    <>
      <button
        type="button"
        data-testid="beta-feedback-launcher"
        onClick={() => {
          setOpen(true);
          trackEvent("feedback_widget_open", { page_url: location.pathname });
        }}
        className="fixed z-40 bottom-5 right-5 inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-medium uppercase tracking-[0.18em] shadow-lg transition-all hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        style={{
          background: "var(--v2-accent-strong)",
          color: "var(--v2-accent-ink)",
          boxShadow: "0 6px 24px rgba(46,38,94,0.25)",
        }}
        aria-label="Send feedback"
      >
        <MessageSquareWarning className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Send Feedback</span>
      </button>
      {open && (
        <FeedbackModal
          user={user}
          pageUrl={location.pathname}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}


function FeedbackModal({ user, pageUrl, onClose }) {
  const [category, setCategory] = useState("bug");
  const [severity, setSeverity] = useState("medium");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const onFile = useCallback((evt) => {
    const f = evt.target.files?.[0];
    if (!f) return;
    if (!/^image\//i.test(f.type)) {
      setError("Screenshot must be an image (PNG, JPEG, WebP, or GIF).");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setError("Screenshot exceeds the 5MB limit.");
      return;
    }
    setError("");
    setFile(f);
  }, []);

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (message.trim().length < 5) {
      setError("Please tell us a bit more (min 5 characters).");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("category", category);
      fd.append("severity", severity);
      fd.append("message", message.trim());
      fd.append("page_url", pageUrl || "");
      if (file) fd.append("screenshot", file);
      await api.post("/feedback", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });
      trackEvent("feedback_widget_submit", { category, severity });
      toast.success("Thanks — we got it. The team reviews feedback daily.");
      onClose();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      data-testid="beta-feedback-modal"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.75)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border p-6"
        style={{ background: "var(--v2-background)", borderColor: "var(--v2-border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--v2-accent)]">Send Feedback</div>
            <h3 className="font-display text-lg text-[var(--v2-text)]">Tell us what you saw</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="beta-feedback-close"
            className="text-[var(--v2-text-muted)] hover:text-[var(--v2-text)]"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4" noValidate>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--v2-text-muted)] mb-2">Category</div>
            <div className="grid grid-cols-4 gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  data-testid={`beta-feedback-category-${c.value}`}
                  onClick={() => setCategory(c.value)}
                  className="rounded-lg border text-xs py-2 px-2 transition-all"
                  style={{
                    background: category === c.value ? "rgba(167,139,250,0.15)" : "transparent",
                    borderColor: category === c.value ? c.color : "var(--v2-border)",
                    color: category === c.value ? c.color : "var(--v2-text-muted)",
                  }}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--v2-text-muted)] mb-2">Severity</div>
            <select
              data-testid="beta-feedback-severity"
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              className="w-full rounded-md border border-[var(--v2-border)] bg-transparent px-3 py-2 text-sm text-[var(--v2-text)]"
              style={{ background: "var(--v2-surface)" }}
            >
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--v2-text-muted)] mb-2">Message</div>
            <textarea
              data-testid="beta-feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              maxLength={4000}
              placeholder="What were you doing? What did you expect to happen? What actually happened?"
              className="w-full rounded-md border border-[var(--v2-border)] bg-transparent px-3 py-2 text-sm text-[var(--v2-text)]"
            />
            <div className="text-[10px] text-[var(--v2-text-muted)] mt-1">
              {message.length} / 4000
              {user?.email && <> · we'll attach <span className="font-mono">{user.email}</span></>}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--v2-text-muted)] mb-2">Screenshot (optional)</div>
            {file ? (
              <div className="flex items-center justify-between rounded-md border border-[var(--v2-border)] px-3 py-2 text-sm text-[var(--v2-text-muted)]">
                <span className="truncate" data-testid="beta-feedback-file-name">{file.name} · {(file.size / 1024).toFixed(0)} KB</span>
                <button
                  type="button"
                  data-testid="beta-feedback-file-remove"
                  onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                  className="text-xs text-[var(--v2-text-muted)] hover:text-[var(--v2-text)] ml-3"
                >
                  Remove
                </button>
              </div>
            ) : (
              <label
                className="flex items-center justify-center gap-2 rounded-md border border-dashed border-[var(--v2-border)] px-3 py-3 text-xs text-[var(--v2-text-muted)] hover:text-[var(--v2-text)] cursor-pointer"
              >
                <Camera className="w-3.5 h-3.5" />
                Attach a screenshot (PNG / JPEG / WebP / GIF · 5MB max)
                <input
                  ref={fileInputRef}
                  data-testid="beta-feedback-screenshot-input"
                  type="file"
                  accept="image/*"
                  onChange={onFile}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {error && (
            <div
              data-testid="beta-feedback-error"
              className="text-sm rounded-md px-3 py-2"
              style={{ background: "rgba(252,165,165,0.08)", border: "1px solid rgba(252,165,165,0.3)", color: "#FCA5A5" }}
            >
              {error}
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-[var(--v2-border)]">
            <div className="text-[10px] text-[var(--v2-text-muted)]">Page: {pageUrl}</div>
            <button
              type="submit"
              data-testid="beta-feedback-submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium uppercase tracking-[0.18em] disabled:opacity-50"
              style={{ background: "var(--v2-accent)", color: "var(--v2-accent-ink)" }}
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {submitting ? "Sending…" : "Send feedback"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
